import { Box, Chip, Tab, Tabs, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import React, { useState } from "react";
import { EdgeDetail } from "../../../services/graphService";
import ClickableEntityName from "../EntityDetailOverlay/ClickableEntityName";
import EntityRelationTable from "../EntityDetailOverlay/EntityRelationTable";

interface EdgeRelationshipTabsProps {
  edgeDetail: EdgeDetail;
  onEntityClick?: (entityId: string) => void;
}

const EdgeRelationshipTabs: React.FC<EdgeRelationshipTabsProps> = ({
  edgeDetail,
  onEntityClick,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Define columns for sources table
  const sourcesColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "title",
      header: "Title",
      size: 300,
      Cell: ({ row }) => (
        <ClickableEntityName
          name={row.original.title}
          entityId={row.original.id}
          onEntityClick={onEntityClick}
          variant="body2"
          fontWeight={500}
        />
      ),
    },
    {
      accessorKey: "domain",
      header: "Domain",
      size: 120,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue() as string}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      ),
    },
    {
      accessorKey: "publication_date",
      header: "Date",
      size: 100,
      Cell: ({ cell }) => formatDate(cell.getValue() as string),
    },
  ];

  // Transform sources data to match EntityRelationTable expectations
  const sourcesData = (edgeDetail.relation_sources || []).map((source) => ({
    ...source,
    name: source.title,
    relationType: "document", // Mark as document type for proper rendering
    relationSummary: source.relation_summary,
  }));

  return (
    <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
      {/* Tab Headers */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab
            label={`Sources (${edgeDetail.relation_sources?.length || 0})`}
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ px: 2, pt: 1, flexGrow: 1, overflow: "auto" }}>
        {activeTab === 0 && (
          <Box>
            {sourcesData.length > 0 ? (
              <EntityRelationTable
                columns={sourcesColumns}
                data={sourcesData}
                pageSize={10}
                enableExpanding={true}
                context="source"
              />
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No sources available for this relationship
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EdgeRelationshipTabs;
